export const LEGAL_VERSION = '2026-10-26';
export const OPERATOR = 'Capital Market Laboratories, LLC';
export const LEGAL_EMAIL = 'support@adorchestrator.com';
export const MAILING_ADDRESS = '2223 Roosevelt Ave Apt. B, Berkeley, CA 94703';

export interface Identity {
  userId?: string;
  demoAccountHidden?: boolean;
  displayName: string;
  email: string;
  trialStartedAt: string;
  trialEndsAt: string;
  trialActive: boolean;
}

export interface IdentityStatus {
  available: boolean;
  legalEmail: string | null;
  mailingAddress: string | null;
  workspaceAccessEnabled?: boolean;
  legalReviewPending?: boolean;
  legalPublished?: boolean;
}

export interface CodeDelivery {
  sent: boolean;
  intent: 'signup' | 'login';
  retryAfterSeconds?: number;
}

export interface PendingVerification {
  pending: boolean;
  emailHint: string | null;
  intent: 'signup' | 'login' | null;
  retryAfterSeconds: number;
}

export class IdentityError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly retryAfterSeconds = 0,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'IdentityError';
  }
}

// Cookies are HttpOnly. Neither codes nor identity/session credentials go into browser storage or URLs.
export async function identityRequest<T>(path: string, body?: unknown): Promise<T> {
  return requestJson<T>(`/api/v1/identity/${path}`, path, body);
}

export async function workspaceRequest<T>(): Promise<T> {
  return requestJson<T>('/api/v1/session', 'workspace');
}

async function requestJson<T>(url: string, path: string, body?: unknown): Promise<T> {
  const controller = new AbortController();
  const timer = globalThis.setTimeout(() => controller.abort(), 25000);
  try {
    const response = await fetch(url, {
      method: body === undefined ? 'GET' : 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers:
        body === undefined
          ? undefined
          : {
              'Content-Type': 'application/json',
              'X-AdOrchestrator-Identity': '1',
            },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });
    if (!response.ok) {
      if (response.status === 409 && path === 'verify') {
        const failure = (await response.json().catch(() => null)) as { code?: string } | null;
        if (failure?.code === 'signup_required')
          throw new IdentityError('Complete signup to continue.', 409, 0, 'signup_required');
      }
      if (response.status === 429)
        throw new IdentityError(
          'Too many attempts. Please wait a few minutes and try again.',
          429,
          Number(response.headers.get('Retry-After')) || 60,
        );
      if (response.status === 503) {
        const failure = (await response.json().catch(() => null)) as { code?: string } | null;
        throw new IdentityError(
          failure?.code === 'email_delivery_unavailable'
            ? 'We couldn’t send your email. Please try again shortly. If it keeps happening, contact support@adorchestrator.com.'
            : 'Account signup is unavailable on this server. Please try again shortly or contact support@adorchestrator.com.',
          503,
        );
      }
      if (response.status === 401)
        throw new IdentityError('Your session has ended. Please log in again.', 401);
      if (response.status === 400 || response.status === 422)
        throw new IdentityError(
          path === 'verify'
            ? 'That code is incorrect or has expired. Check your email or request a new code.'
            : 'Check your email address, name and agreement to the current terms, then try again.',
          response.status,
        );
      throw new IdentityError(
        'We couldn’t complete that request. Please try again.',
        response.status,
      );
    }
    return (await response.json()) as T;
  } catch (cause) {
    if (cause instanceof IdentityError) throw cause;
    throw new IdentityError(
      cause instanceof Error && cause.name === 'AbortError'
        ? 'That took longer than expected. Check your connection and try again.'
        : 'We couldn’t reach the account service. Check your connection and try again.',
      0,
    );
  } finally {
    globalThis.clearTimeout(timer);
  }
}
